import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectStrategicAxes"]["request"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          projectStrategicAxes: [],
          request: setRequestStateToPending(),
          totalWeight: 0,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectStrategicAxes: [],
          request: setRequestStateToPending(),
          totalWeight: 0,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    projectStrategicAxes,
  }: Boundaries["retrieveProjectStrategicAxes"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          projectStrategicAxes,
          totalWeight: projectStrategicAxes.reduce(
            (acc, value) => acc + value.weight * value.consistencyValue,
            0,
          ),
          request: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectStrategicAxes,
          totalWeight: projectStrategicAxes.reduce(
            (acc, value) => acc + value.weight * value.consistencyValue,
            0,
          ),
          request: setRequestStateToSuccess(),
        },
      ];
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveProjectStrategicAxes"]["failure"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          projectStrategicAxes: [],
          request: setRequestStateToFailure(),
          totalWeight: 0,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectStrategicAxes: [],
          request: setRequestStateToFailure(),
          totalWeight: 0,
        },
      ];
}

function handleCreateProjectStrategicAxisRequest(
  state: State,
  {
    project,
    projectStrategicAxis,
  }: Boundaries["createProjectStrategicAxis"]["request"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          projectStrategicAxes: [
            ...state[index].projectStrategicAxes,
            {
              ...projectStrategicAxis,
              uuid: projectStrategicAxis.strategicAxisId,
            },
          ],
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectStrategicAxes: [
            {
              ...projectStrategicAxis,
              uuid: projectStrategicAxis.strategicAxisId,
            },
          ],
          request: setRequestStateToPending(),
          totalWeight: 0,
        },
      ];
}

function handleCreateProjectStrategicAxisSuccess(
  state: State,
  {
    project,
    projectStrategicAxis,
  }: Boundaries["createProjectStrategicAxis"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          totalWeight: state[index].projectStrategicAxes.reduce(
            (acc, value) => acc + value.weight * value.consistencyValue,
            0,
          ),
          projectStrategicAxes: [
            projectStrategicAxis,
            ...state[index].projectStrategicAxes.filter(
              ({ uuid }) => uuid !== projectStrategicAxis.strategicAxisId,
            ),
          ],
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectStrategicAxes: [
            {
              ...projectStrategicAxis,
              uuid: projectStrategicAxis.strategicAxisId,
            },
          ],
          request: setRequestStateToSuccess(),
          totalWeight: 0,
        },
      ];
}

function handleCreateProjectStrategicAxisFailure(
  state: State,
  {
    project,
    projectStrategicAxis,
  }: Boundaries["createProjectStrategicAxis"]["failure"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return [
    ...state.slice(0, index),
    {
      ...state[index],
      projectStrategicAxes: state[index].projectStrategicAxes.filter(
        ({ uuid }) => uuid !== projectStrategicAxis.strategicAxisId,
      ),
    },
    ...state.slice(index + 1),
  ];
}

function handleEditProjectStrategicAxisRequest(
  state: State,
  {
    projectStrategicAxis,
    nextProjectStrategicAxis,
  }: Boundaries["editProjectStrategicAxis"]["request"],
): State {
  return state.map((res) => ({
    ...res,
    projectStrategicAxes: res.projectStrategicAxes.map((p) =>
      p.uuid === projectStrategicAxis.uuid
        ? { ...projectStrategicAxis, ...nextProjectStrategicAxis }
        : p,
    ),
  }));
}

function handleEditProjectStrategicAxisSuccess(
  state: State,
  { projectStrategicAxis }: Boundaries["editProjectStrategicAxis"]["success"],
): State {
  return state.map((res) => ({
    ...res,
    totalWeight: res.projectStrategicAxes.reduce(
      (acc, value) => acc + value.weight * value.consistencyValue,
      0,
    ),
  }));
}

function handleEditProjectStrategicAxisFailure(
  state: State,
  { projectStrategicAxis }: Boundaries["editProjectStrategicAxis"]["failure"],
): State {
  return state.map((res) => ({
    ...res,
    projectStrategicAxes: res.projectStrategicAxes.map((p) =>
      p.uuid === projectStrategicAxis.uuid ? projectStrategicAxis : p,
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectStrategicAxesRequested]: handleRequest,
    [events.retrieveProjectStrategicAxesSucceeded]: handleSuccess,
    [events.retrieveProjectStrategicAxesFailed]: handleFailure,
    [events.createProjectStrategicAxisRequested]:
      handleCreateProjectStrategicAxisRequest,
    [events.createProjectStrategicAxisSucceeded]:
      handleCreateProjectStrategicAxisSuccess,
    [events.createProjectStrategicAxisFailed]:
      handleCreateProjectStrategicAxisFailure,
    [events.editProjectStrategicAxisRequested]:
      handleEditProjectStrategicAxisRequest,
    [events.editProjectStrategicAxisSucceeded]:
      handleEditProjectStrategicAxisSuccess,
    [events.editProjectStrategicAxisFailed]:
      handleEditProjectStrategicAxisFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
